import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/cliente';
import type { Archivo } from '../api/tipos';
import { Aviso, Campo, Seleccion, Tarjeta, Vacio } from './piezas';

/**
 * El editor de planos interactivos de un inmueble.
 *
 * <p>Un plano con zonas es el mapa del recorrido: se ve la distribución de un
 * vistazo y, al pulsar una habitación, el visitante entra a esa escena 360°. La
 * API lo tenía desde el principio —planos, zonas, polígonos y el enlace a la
 * escena— y no había forma de dibujarlo: sin pantalla, definir un polígono a
 * mano significaba escribir una lista de coordenadas.
 *
 * <p>Las zonas se dibujan <b>pulsando sobre el plano</b>. Cada pulsación añade
 * un vértice, y el polígono se cierra cuando hay al menos tres. Los vértices se
 * guardan en porcentaje del ancho y del alto, no en píxeles: así el mismo plano
 * se puede pintar a cualquier tamaño —en el panel grande, en el portal pequeño,
 * en un móvil— y las zonas siguen cuadrando.
 */

export interface ZonaPlano {
  id?: string;
  nombre: string;
  /** Vértices en porcentaje: `[{ x: 12.5, y: 40 }, …]`. */
  poligono: { x: number; y: number }[];
  escenaId: string | null;
  areaM2: number | null;
  color: string | null;
}

export interface Plano {
  id: string;
  nombre: string;
  orden: number;
  imagen: string | null;
  anchoPx: number | null;
  altoPx: number | null;
  zonas: ZonaPlano[];
}

/** Lo que hace falta de una escena para enlazarla y para ubicarla. */
export interface EscenaEnlazable {
  id: string;
  nombre: string;
  planoId: string | null;
  planoX: number | null;
  planoY: number | null;
}

const COLOR_POR_OMISION = '#7FB0E6';

export default function EditorPlanos({
  inmuebleId,
  planos,
  escenas,
  editable,
}: {
  inmuebleId: string;
  planos: Plano[];
  escenas: EscenaEnlazable[];
  editable: boolean;
}) {
  const cache = useQueryClient();
  const entrada = useRef<HTMLInputElement>(null);
  const imagen = useRef<HTMLImageElement>(null);

  const [error, setError] = useState<unknown>(null);
  const [exito, setExito] = useState<string | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [activo, setActivo] = useState<string | null>(null);

  /*
   * Dos cosas distintas se dibujan sobre el mismo plano.
   *
   * Las **zonas** son poligonos que responden «que habitacion es esta»: se
   * pulsa una y el visitante entra a su escena. Los **puntos** responden «donde
   * estoy»: uno por escena, que marca donde esta la camara. Un recorrido se
   * entiende con los dos, y se dibujan igual --pulsando-- asi que el modo dice
   * cual se esta poniendo.
   */
  const [modo, setModo] = useState<'zona' | 'punto'>('zona');

  /** Los vértices que se están poniendo ahora. Vacío = no se está dibujando. */
  const [vertices, setVertices] = useState<{ x: number; y: number }[]>([]);
  const [nombreZona, setNombreZona] = useState('');
  const [escenaZona, setEscenaZona] = useState('');

  /** Que escena se esta ubicando, en el modo punto. */
  const [escenaPunto, setEscenaPunto] = useState('');

  const plano = planos.find((p) => p.id === activo) ?? planos[0];

  const refrescar = (mensaje: string) => {
    setError(null);
    setExito(mensaje);
    void cache.invalidateQueries({ queryKey: ['recorrido', inmuebleId] });
  };

  const subir = async (archivos: FileList | null) => {
    const archivo = archivos?.[0];
    if (!archivo) return;
    setSubiendo(true);
    setError(null);
    try {
      const formulario = new FormData();
      formulario.append('archivo', archivo);
      const subido = await api<Archivo>('/admin/archivos?tipo=PLANO', {
        metodo: 'POST',
        formulario,
      });
      await api<unknown>(`/admin/inmuebles/${inmuebleId}/recorrido/planos`, {
        metodo: 'POST',
        cuerpo: {
          archivoId: subido.id,
          nombre: nombreNuevo.trim() || archivo.name.replace(/\.[^.]+$/, ''),
          zonas: [],
        },
      });
      setNombreNuevo('');
      refrescar('Plano añadido. La imagen se está procesando.');
    } catch (e) {
      setError(e);
      setExito(null);
    } finally {
      setSubiendo(false);
      if (entrada.current) entrada.current.value = '';
    }
  };

  /**
   * Guarda el plano con la lista completa de zonas.
   *
   * <p>La API guarda las zonas junto al plano y no una por una: el cuerpo lleva
   * siempre todas. Así que añadir o quitar una es mandar la lista resultante.
   */
  const guardarZonas = useMutation({
    mutationFn: (zonas: ZonaPlano[]) =>
      api<unknown>(`/admin/inmuebles/${inmuebleId}/recorrido/planos/${plano?.id}`, {
        metodo: 'PUT',
        cuerpo: {
          nombre: plano?.nombre,
          anchoPx: plano?.anchoPx,
          altoPx: plano?.altoPx,
          zonas: zonas.map((z) => ({
            nombre: z.nombre,
            poligono: z.poligono,
            escenaId: z.escenaId,
            areaM2: z.areaM2,
            color: z.color ?? COLOR_POR_OMISION,
          })),
        },
      }),
    onSuccess: () => {
      setVertices([]);
      setNombreZona('');
      setEscenaZona('');
      refrescar('Zonas guardadas.');
    },
    onError: (e) => setError(e),
  });

  /**
   * Ubica una escena sobre el plano.
   *
   * <p>Las tres van juntas --plano y las dos coordenadas--: media ubicacion no
   * se puede pintar, y la base lo impide con una restriccion. El nombre viaja
   * porque el comando lo exige; mandarlo vacio borraria el de la escena.
   */
  const ubicarEscena = useMutation({
    mutationFn: (datos: { escenaId: string; x: number; y: number }) => {
      const escena = escenas.find((e) => e.id === datos.escenaId);
      return api<unknown>(
        `/admin/inmuebles/${inmuebleId}/recorrido/escenas/${datos.escenaId}`,
        {
          metodo: 'PUT',
          cuerpo: {
            nombre: escena?.nombre,
            planoId: plano?.id,
            planoX: datos.x,
            planoY: datos.y,
          },
        },
      );
    },
    onSuccess: () => refrescar('Escena ubicada sobre el plano.'),
    onError: (e) => setError(e),
  });

  const borrarPlano = useMutation({
    mutationFn: (planoId: string) =>
      api<unknown>(`/admin/inmuebles/${inmuebleId}/recorrido/planos/${planoId}`, {
        metodo: 'DELETE',
      }),
    onSuccess: () => {
      setActivo(null);
      refrescar('Plano eliminado.');
    },
    onError: (e) => setError(e),
  });

  /** Donde se pulsó, en porcentaje del plano. Nulo si fue fuera. */
  const puntoPulsado = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imagen.current) return null;
    const caja = imagen.current.getBoundingClientRect();
    const x = ((e.clientX - caja.left) / caja.width) * 100;
    const y = ((e.clientY - caja.top) / caja.height) * 100;
    if (x < 0 || x > 100 || y < 0 || y > 100) return null;
    return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
  };

  const alPulsarPlano = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!editable) return;
    const p = puntoPulsado(e);
    if (!p) return;

    if (modo === 'punto') {
      if (!escenaPunto || !plano) return;
      ubicarEscena.mutate({ escenaId: escenaPunto, x: p.x, y: p.y });
      return;
    }
    setVertices((previos) => [...previos, p]);
  };

  const cerrarZona = () => {
    if (!plano || vertices.length < 3) return;
    guardarZonas.mutate([
      ...plano.zonas,
      {
        nombre: nombreZona.trim() || `Zona ${plano.zonas.length + 1}`,
        poligono: vertices,
        escenaId: escenaZona || null,
        areaM2: null,
        color: COLOR_POR_OMISION,
      },
    ]);
  };

  const quitarZona = (indice: number) => {
    if (!plano) return;
    guardarZonas.mutate(plano.zonas.filter((_, i) => i !== indice));
  };

  const puntos = (poligono: { x: number; y: number }[]) =>
    poligono.map((p) => `${p.x},${p.y}`).join(' ');

  return (
    <Tarjeta titulo="Planos interactivos">
      <Aviso error={error} exito={exito} />

      <p className="campo__ayuda" style={{ marginTop: 0 }}>
        El plano es el mapa del recorrido: el visitante ve la distribución y, al pulsar una
        habitación, entra a esa escena 360°.
      </p>

      {editable && (
        <div className="rejilla" style={{ marginTop: 12 }}>
          <Campo
            etiqueta="Nombre del plano"
            valor={nombreNuevo}
            alCambiar={setNombreNuevo}
            ayuda="Primer piso, segundo piso… Si se deja vacío se usa el nombre del archivo."
          />
          <div>
            <label>Imagen del plano</label>
            <input
              ref={entrada}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              disabled={subiendo}
              onChange={(e) => void subir(e.target.files)}
            />
            <p className="campo__ayuda">{subiendo ? 'Subiendo…' : 'El plano en planta, de frente.'}</p>
          </div>
        </div>
      )}

      {planos.length === 0 ? (
        <Vacio>
          Este inmueble no tiene planos. Sube uno y dibuja las zonas pulsando sobre él.
        </Vacio>
      ) : (
        <>
          {planos.length > 1 && (
            <div className="fila" style={{ flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              {planos.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={p.id === plano?.id ? 'secundario' : 'plano'}
                  onClick={() => {
                    setActivo(p.id);
                    setVertices([]);
                  }}
                >
                  {p.nombre}
                  {!p.imagen && ' · procesando'}
                </button>
              ))}
            </div>
          )}

          {plano && !plano.imagen && (
            <Vacio>
              La imagen del plano se está procesando. Esta pantalla se actualiza sola en cuanto
              esté lista.
            </Vacio>
          )}

          {plano?.imagen && (
            <>
              {/*
                El plano y sus zonas, en la misma caja. El SVG va encima con el
                mismo sistema de coordenadas que los polígonos --de 0 a 100 en
                los dos ejes-- así que no hay que convertir nada: los vértices
                guardados se pintan tal cual a cualquier tamaño.
              */}
              {editable && (
                <div className="fila" style={{ marginTop: 14, gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className={modo === 'zona' ? 'secundario' : 'plano'}
                    onClick={() => setModo('zona')}
                  >
                    Dibujar zonas
                  </button>
                  <button
                    type="button"
                    className={modo === 'punto' ? 'secundario' : 'plano'}
                    onClick={() => {
                      setModo('punto');
                      setVertices([]);
                    }}
                  >
                    Ubicar los puntos 360°
                  </button>
                </div>
              )}

              {editable && modo === 'punto' && (
                <div style={{ marginTop: 10, maxWidth: 420 }}>
                  {escenas.length === 0 ? (
                    <p className="campo__ayuda">
                      Hace falta al menos una escena 360° para poder ubicarla.
                    </p>
                  ) : (
                    <>
                      <Seleccion
                        etiqueta="Escena a ubicar"
                        valor={escenaPunto}
                        alCambiar={setEscenaPunto}
                        vacio="Elige la escena"
                        opciones={escenas.map((e) => ({
                          valor: e.id,
                          texto: e.planoId ? `${e.nombre} · ya ubicada` : e.nombre,
                        }))}
                      />
                      <p className="campo__ayuda">
                        {escenaPunto
                          ? 'Pulsa sobre el plano donde está esa cámara. Volver a pulsar la mueve.'
                          : 'Elige una escena y pulsa sobre el plano donde está su cámara.'}
                      </p>
                    </>
                  )}
                </div>
              )}

              <div
                className="plano-lienzo"
                style={{ marginTop: 14, cursor: editable ? 'crosshair' : 'default' }}
                onClick={alPulsarPlano}
              >
                <img ref={imagen} src={plano.imagen} alt={plano.nombre} />

                <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                  {plano.zonas.map((z, i) => (
                    <polygon
                      key={z.id ?? i}
                      className="plano-zona"
                      points={puntos(z.poligono)}
                      fill={z.color ?? COLOR_POR_OMISION}
                    />
                  ))}

                  {vertices.length > 0 && (
                    <>
                      <polygon className="plano-zona plano-zona--nueva" points={puntos(vertices)} />
                      {vertices.map((v, i) => (
                        <circle key={i} className="plano-vertice" cx={v.x} cy={v.y} r={1.1} />
                      ))}
                    </>
                  )}
                </svg>

                {/* Los puntos 360: uno por escena ubicada en ESTE plano. Van
                    fuera del SVG por lo mismo que los nombres —dentro se
                    deformarían— y porque asi son elementos normales. */}
                {escenas
                  .filter((e) => e.planoId === plano.id && e.planoX != null && e.planoY != null)
                  .map((e) => (
                    <span
                      key={`p-${e.id}`}
                      className={`plano-punto${escenaPunto === e.id ? ' plano-punto--activo' : ''}`}
                      style={{ left: `${e.planoX}%`, top: `${e.planoY}%` }}
                      title={e.nombre}
                    >
                      <span className="plano-punto__nombre">{e.nombre}</span>
                    </span>
                  ))}

                {/* Los nombres van fuera del SVG: con `preserveAspectRatio="none"`
                    el texto dentro saldría estirado. */}
                {plano.zonas.map((z, i) => {
                  const centro = z.poligono.reduce(
                    (acc, p) => ({ x: acc.x + p.x / z.poligono.length, y: acc.y + p.y / z.poligono.length }),
                    { x: 0, y: 0 },
                  );
                  return (
                    <span
                      key={z.id ?? `n-${i}`}
                      className="plano-etiqueta"
                      style={{ left: `${centro.x}%`, top: `${centro.y}%` }}
                    >
                      {z.nombre}
                    </span>
                  );
                })}
              </div>

              {editable && modo === 'zona' && (
                <div style={{ marginTop: 12 }}>
                  {vertices.length === 0 ? (
                    <p className="campo__ayuda" style={{ marginTop: 0 }}>
                      Pulsa sobre el plano para marcar las esquinas de una zona. Con tres o más
                      esquinas se puede guardar.
                    </p>
                  ) : (
                    <div
                      style={{
                        padding: 12,
                        border: '1px solid var(--linea)',
                        borderRadius: 8,
                        background: 'var(--fondo)',
                      }}
                    >
                      <p className="campo__ayuda" style={{ marginTop: 0 }}>
                        {vertices.length} esquina{vertices.length === 1 ? '' : 's'} marcada
                        {vertices.length === 1 ? '' : 's'}.
                        {vertices.length < 3 && ' Hacen falta al menos tres.'}
                      </p>

                      <div className="rejilla">
                        <Campo
                          etiqueta="Nombre de la zona"
                          valor={nombreZona}
                          alCambiar={setNombreZona}
                          ayuda="Sala, cocina, alcoba principal…"
                        />
                        {escenas.length > 0 && (
                          <Seleccion
                            etiqueta="Lleva a la escena"
                            valor={escenaZona}
                            alCambiar={setEscenaZona}
                            vacio="Ninguna, solo marcar"
                            ayuda="Al pulsar esta zona, el visitante entra a esa escena 360°."
                            opciones={escenas.map((e) => ({ valor: e.id, texto: e.nombre }))}
                          />
                        )}
                      </div>

                      <div className="fila" style={{ marginTop: 10 }}>
                        <button
                          type="button"
                          className="secundario"
                          disabled={vertices.length < 3 || guardarZonas.isPending}
                          onClick={cerrarZona}
                        >
                          Guardar la zona
                        </button>
                        <button
                          type="button"
                          className="plano"
                          onClick={() => setVertices((v) => v.slice(0, -1))}
                        >
                          Quitar la última esquina
                        </button>
                        <button type="button" className="plano" onClick={() => setVertices([])}>
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {escenas.length > 0 && (
                <p className="campo__ayuda" style={{ marginTop: 12 }}>
                  {(() => {
                    const ubicadas = escenas.filter((e) => e.planoId === plano.id).length;
                    if (ubicadas === escenas.length) {
                      return `Las ${escenas.length} escenas están ubicadas sobre este plano.`;
                    }
                    return `${ubicadas} de ${escenas.length} escenas ubicadas sobre este plano.`;
                  })()}
                </p>
              )}

              {plano.zonas.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <h3 style={{ fontSize: '0.78rem', textTransform: 'uppercase' }}>
                    Zonas de este plano
                  </h3>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.88rem' }}>
                    {plano.zonas.map((z, i) => (
                      <li key={z.id ?? i} style={{ marginBottom: 4 }}>
                        {z.nombre}
                        {z.escenaId && (
                          <> · lleva a {escenas.find((e) => e.id === z.escenaId)?.nombre ?? 'una escena'}</>
                        )}
                        {editable && (
                          <button
                            type="button"
                            className="plano"
                            style={{ marginLeft: 8 }}
                            onClick={() => quitarZona(i)}
                          >
                            Quitar
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {editable && (
                <button
                  type="button"
                  className="plano"
                  style={{ marginTop: 12 }}
                  onClick={() => borrarPlano.mutate(plano.id)}
                >
                  Eliminar el plano
                </button>
              )}
            </>
          )}
        </>
      )}
    </Tarjeta>
  );
}
